/**
 * FacebookPanel — pestaña Facebook de Redes Orgánicas.
 *
 * Datos: /api/meta/organic/facebook (Graph API oficial de la Página): perfil,
 * Page Insights del periodo con su serie diaria, visualizaciones separadas de la
 * pauta, video, reseñas y publicaciones con reacciones y comentarios.
 *
 * ORGÁNICO DE VERDAD: las visualizaciones de la Página (page_media_view) vienen
 * con breakdown is_from_ads; aquí solo se muestra lo orgánico y la pauta va en
 * un aviso de una línea. En Cantabria >99% de las visualizaciones era pauta.
 *
 * LO QUE ESTA PESTAÑA NO MUESTRA, Y NO ES UN OLVIDO: Meta eliminó de la API de
 * Página el alcance, las impresiones y la demografía de fans, y el TEXTO de los
 * comentarios exige pages_read_user_content (el conteo sí se puede leer).
 * Pintar esos huecos con ceros sería mentir. Ver la nota en meta_client.py.
 */
import React, { useCallback, useMemo, useState } from 'react';
import {
  FaFacebook, FaUsers, FaRegThumbsUp, FaPlay, FaShare, FaExclamationTriangle,
  FaChartBar, FaTrophy, FaMousePointer, FaHeart, FaEye, FaComment, FaVideo,
  FaStar, FaRegClock, FaQuoteLeft, FaPercent,
} from 'react-icons/fa';
import {
  numFmt, compact, pct1, pct2, filterByRange, insightWindowLabel, Kpi, longDate, shortText,
} from '../../components/SocialApi/socialApiShared';
import {
  PeriodSummary, PostsSection, PostCard, PostsTable, PostModal, MetricGrid,
  StackedByDateChart, DailyChart, TopChart, DonutCard, chartBox, useDayPick, PaidNote, engagementRate,
  MonthlyFollowersCard,
} from './organicShared';

// Page Insights admite hasta ~90 días por request (backend: FB_INSIGHTS_MAX_DAYS).
const FB_INSIGHTS_MAX_DAYS = 90;

const TYPE_META = {
  PHOTO: 'typePhoto', VIDEO: 'typeVideo', ALBUM: 'typeAlbum',
  LINK: 'typeLink', EVENT: 'typeEvent', STATUS: 'typeStatus',
};

// Las CLAVES son las que devuelve Meta en post_reactions_by_type_total /
// page_actions_post_reactions_total, que NO son las del selector visible:
// 'sorry' (no 'sad') y 'anger' (no 'angry'). Con las equivocadas el desglose
// sale vacío sin error.
const REACTIONS = [
  { key: 'like',     i18n: 'rxLike',     color: '#1877F2', emoji: '👍' },
  { key: 'love',     i18n: 'rxLove',     color: '#ec4899', emoji: '❤️' },
  { key: 'care',     i18n: 'rxCare',     color: '#f59e0b', emoji: '🤗' },
  { key: 'haha',     i18n: 'rxHaha',     color: '#F68D2E', emoji: '😆' },
  { key: 'wow',      i18n: 'rxWow',      color: '#8b5cf6', emoji: '😮' },
  { key: 'sorry',    i18n: 'rxSorry',    color: '#64748b', emoji: '😢' },
  { key: 'anger',    i18n: 'rxAnger',    color: '#ef4444', emoji: '😡' },
  { key: 'thankful', i18n: 'rxThankful', color: '#10b981', emoji: '🌸' },
  { key: 'pride',    i18n: 'rxPride',    color: '#a855f7', emoji: '🏳️‍🌈' },
];

// Interacción de una publicación = reacciones + comentarios + compartidos + clics.
const inter = (p) => (p.reactions || 0) + (p.comments || 0) + (p.shares || 0) + (p.clicks || 0);

export default function FacebookPanel({ data, range, onRangeChange, username, t }) {
  const [modalPost, setModalPost] = useState(null);
  const typeLabel = useCallback((p) => t(`organic.${TYPE_META[p.type] || 'typeOther'}`), [t]);

  const page = data?.page || {};
  const ins = data?.insights || {};
  const daily = data?.daily || [];
  const netFollows = ins.net_follows;
  // "Seguidores" es lo que el usuario reconoce; los fans ("Me gusta" de la
  // Página) son el respaldo si Meta no manda followers.
  const followers = page.followers ?? page.fans;

  const posts = useMemo(
    () => filterByRange(data?.posts || [], range).map((p) => ({ ...p, inter: inter(p) })),
    [data, range]);
  const dayPick = useDayPick(posts, setModalPost);
  const best = useMemo(() => posts.reduce((b, p) => (!b || p.inter > b.inter ? p : b), null), [posts]);
  const sum = (k) => posts.reduce((a, p) => a + (p[k] || 0), 0);
  const avgInter = posts.length ? Math.round(sum('inter') / posts.length) : 0;
  const insightSub = insightWindowLabel(range, FB_INSIGHTS_MAX_DAYS, t);
  const hasComments = posts.some((p) => p.comments != null);

  // Reacciones a nivel PÁGINA del periodo: incluye las que llegaron a
  // publicaciones anteriores al rango (el ⓘ lo explica).
  const pageReactions = useMemo(() => {
    const raw = ins.reactions_by_type;
    if (!raw) return [];
    return REACTIONS.filter((r) => raw[r.key])
      .map((r) => ({ label: t(`organic.${r.i18n}`), color: r.color, emoji: r.emoji, value: Number(raw[r.key]) || 0 }))
      .sort((a, b) => b.value - a.value);
  }, [ins.reactions_by_type, t]);

  const technicalError = data?.token_error || page.error || data?.insights_error || data?.views_error
    || data?.posts_error || data?.post_metrics_error;

  const sorts = useMemo(() => [
    { id: 'recent', label: t('organic.sortRecent'), fn: (a, b) => new Date(b.timestamp) - new Date(a.timestamp) },
    { id: 'top', label: t('organic.sortTop'), fn: (a, b) => b.inter - a.inter },
  ], [t]);

  const cardMetrics = (p) => [
    { icon: <FaRegThumbsUp />, value: numFmt.format(p.reactions || 0), title: t('organic.reactions') },
    ...(p.comments != null ? [{ icon: <FaComment />, value: numFmt.format(p.comments), title: t('organic.comments') }] : []),
    { icon: <FaShare />, value: numFmt.format(p.shares || 0), title: t('organic.shares') },
  ];

  const hasVideo = ins.video_organic != null || ins.video_30s_organic != null;

  return (
    <>
      <section className="soc-kpis">
        <Kpi icon={<FaUsers />} accent="#1877F2" label={t('organic.followers')} value={compact(followers)}
             sub={netFollows != null ? t('organic.netInPeriod', { n: `${netFollows > 0 ? '+' : ''}${numFmt.format(netFollows)}` }) : null}
             hint={netFollows != null
               ? `${t('organic.followersHint')} ${t('organic.gainedLost', { gained: numFmt.format(ins.follows || 0), lost: numFmt.format(ins.unfollows || 0) })}`
               : t('organic.followersHint')} />
        <Kpi icon={<FaEye />} accent="#3b82f6" label={t('organic.organicViews')} value={compact(ins.views_organic)} sub={insightSub} hint={t('organic.organicViewsFbHint')} />
        <Kpi icon={<FaRegThumbsUp />} accent="#8b5cf6" label={t('organic.interactions')} value={compact(ins.engagements)} sub={insightSub} hint={t('organic.interactionsHintFb')} />
        <Kpi icon={<FaPercent />} accent="#F68D2E" label={t('organic.engagement')} value={pct2(engagementRate(posts, followers))}
             sub={posts.length ? t('organic.engagementSub', { n: numFmt.format(posts.length) }) : t('organic.engagementNone')}
             hint={t('organic.engagementHint')} />
        <Kpi icon={<FaMousePointer />} accent="#10b981" label={t('organic.pageVisits')} value={compact(ins.page_views)} sub={insightSub} hint={t('organic.pageVisitsHint')} />
      </section>

      {ins.views_paid > 0 && <PaidNote t={t} n={compact(ins.views_paid)} />}

      {technicalError && (
        <div className="soc-warn" title={technicalError}><FaExclamationTriangle /> {t('organic.partialData')}</div>
      )}

      <PeriodSummary
        t={t}
        stats={[
          { label: t('organic.statPosts'), value: numFmt.format(posts.length) },
          { label: t('organic.comments'), value: hasComments ? numFmt.format(sum('comments')) : '—' },
          { label: t('organic.statShares'), value: numFmt.format(sum('shares')) },
          { label: t('organic.statAvg'), value: posts.length ? numFmt.format(avgInter) : '—' },
        ]}
        best={best && best.inter > 0 ? best : null}
        bestTitle={t('organic.bestPost')}
        bestMetric={t('organic.nInteractions', { n: numFmt.format(best?.inter || 0) })}
        placeholderIcon={<FaFacebook />}
        onOpen={setModalPost}
      />

      <div className="soc-grid">
        {daily.length > 0 && (
          <section className="soc-chart-card col-8">
            <div className="soc-chart-head">
              <span className="soc-chart-title"><FaChartBar /> {t('organic.fbDaily')}</span>
              <span className="soc-chart-tag">{t('organic.nDays', { n: daily.length })}</span>
            </div>
            {chartBox(<DailyChart days={daily} series={[
              { key: 'views_organic', label: t('organic.organicViews'), color: '#3b82f6' },
              { key: 'engagements', label: t('organic.interactions'), color: '#8b5cf6' },
              { key: 'page_views', label: t('organic.visits'), color: '#10b981' },
            ]} />)}
          </section>
        )}
        <DonutCard title={<><FaHeart /> {t('organic.fbReactions')}</>} hint={t('organic.fbReactionsHint')}
          slices={pageReactions} centerLabel={t('organic.reactionsWord')} emptyText={t('organic.fbReactionsNone')} />
      </div>

      <div className="soc-grid">
        <MonthlyFollowersCard username={username} network="facebook" color="#1877F2" t={t} />
      </div>

      {posts.length > 0 && (
        <div className="soc-grid">
          <section className="soc-chart-card col-8">
            <div className="soc-chart-head">
              <span className="soc-chart-title"><FaChartBar /> {t('organic.chartInterByPost')}</span>
              <span className="soc-chart-tag">{t('organic.byDate')}</span>
            </div>
            {chartBox(<StackedByDateChart items={posts} captionOf={(p) => p.message} onPick={dayPick.pick} series={[
              { key: 'reactions', label: t('organic.reactions'), color: '#1877F2' },
              { key: 'comments', label: t('organic.comments'), color: '#8b5cf6' },
              { key: 'shares', label: t('organic.shares'), color: '#F68D2E' },
              { key: 'clicks', label: t('organic.clicks'), color: '#073C5C' },
            ]} />)}
          </section>
          <section className="soc-chart-card col-4">
            <div className="soc-chart-head"><span className="soc-chart-title"><FaTrophy /> {t('organic.chartTopPosts')}</span></div>
            {chartBox(<TopChart items={posts} t={t} valueOf={(p) => p.inter} captionOf={(p) => p.message} color="#1877F2" onOpen={setModalPost}
              tooltipLabel={(p) => t('organic.nInteractions', { n: numFmt.format(p.inter) })} />)}
          </section>
        </div>
      )}

      {(hasVideo || data?.reviews) && (
        <div className="soc-grid">
          {hasVideo && (
            <section className="soc-chart-card col-4">
              <div className="soc-chart-head"><span className="soc-chart-title"><FaVideo /> {t('organic.videoTitle')}</span></div>
              <MetricGrid items={[
                { label: t('organic.videoOrganicPlays'), value: compact(ins.video_organic) },
                { label: t('organic.video30s'), value: compact(ins.video_30s_organic),
                  hint: ins.video_organic ? t('organic.video30sHint', { pct: pct1((ins.video_30s_organic || 0) / ins.video_organic) }) : null },
              ]} />
            </section>
          )}
          {data?.reviews && <ReviewsCard reviews={data.reviews} t={t} wide={!hasVideo} />}
        </div>
      )}

      <PostsSection
        t={t}
        title={t('organic.postsTitle')}
        icon={<FaFacebook />}
        items={dayPick.items}
        day={dayPick.day}
        onClearDay={dayPick.clear}
        sectionRef={dayPick.sectionRef}
        sorts={sorts}
        emptyText={t('organic.emptyPosts')}
        range={range}
        onRangeChange={onRangeChange}
        renderGrid={(list) => (
          <div className="soc-posts">
            {list.map((p) => (
              <PostCard key={p.id} post={p} t={t} onOpen={setModalPost} badge={typeLabel(p)}
                metrics={cardMetrics(p)} placeholderIcon={<FaFacebook />} />
            ))}
          </div>
        )}
        renderTable={(list) => (
          <PostsTable posts={list} t={t} onOpen={setModalPost} firstLabel={t('organic.colPost')} columns={[
            { label: t('organic.colType'), render: (p) => typeLabel(p) },
            { label: t('organic.views'), render: (p) => (p.views != null ? numFmt.format(p.views) : '—') },
            { label: t('organic.reactions'), render: (p) => numFmt.format(p.reactions || 0) },
            { label: t('organic.comments'), render: (p) => (p.comments != null ? numFmt.format(p.comments) : '—') },
            { label: t('organic.shares'), render: (p) => numFmt.format(p.shares || 0) },
            { label: t('organic.colInter'), render: (p) => numFmt.format(p.inter), strong: true },
          ]} />
        )}
      />

      {modalPost && (
        <PostModal t={t} post={modalPost} onClose={() => setModalPost(null)} network="Facebook"
          badge={typeLabel(modalPost)} metrics={[]} placeholderIcon={<FaFacebook />}>
          <FbPostMetrics post={modalPost} t={t} />
          {modalPost.reactions_by_type && (
            <div className="fb-rx-chips">
              {REACTIONS.filter((r) => modalPost.reactions_by_type[r.key]).map((r) => (
                <span className="fb-rx-chip" key={r.key} title={t(`organic.${r.i18n}`)}>
                  <span aria-hidden="true">{r.emoji}</span> {numFmt.format(modalPost.reactions_by_type[r.key])}
                </span>
              ))}
            </div>
          )}
        </PostModal>
      )}
    </>
  );
}

function FbPostMetrics({ post, t }) {
  const dash = (v) => (v == null ? '—' : numFmt.format(v));
  // Meta devuelve video_views = 0 también en fotos y álbumes: se decide por tipo.
  const isVideo = post.type === 'VIDEO';
  return (
    <MetricGrid compact items={[
      { icon: <FaEye />, label: t('organic.views'), value: dash(post.views) },
      { icon: <FaUsers />, label: t('organic.viewers'), value: dash(post.viewers) },
      { icon: <FaRegThumbsUp />, label: t('organic.reactions'), value: dash(post.reactions) },
      { icon: <FaComment />, label: t('organic.comments'), value: dash(post.comments) },
      { icon: <FaShare />, label: t('organic.shares'), value: dash(post.shares) },
      { icon: <FaMousePointer />, label: t('organic.clicks'), value: dash(post.clicks) },
      ...(isVideo ? [
        { icon: <FaPlay />, label: t('organic.videoViews'), value: dash(post.video_views) },
        { icon: <FaRegClock />, label: t('organic.avgWatchTime'), value: post.video_avg_sec != null ? `${post.video_avg_sec} s` : '—' },
      ] : []),
    ]} />
  );
}

function ReviewsCard({ reviews, t, wide }) {
  const pct = reviews.total ? Math.round((reviews.positive / reviews.total) * 100) : null;
  return (
    <section className={`soc-chart-card ${wide ? 'col-12' : 'col-8'}`}>
      <div className="soc-chart-head"><span className="soc-chart-title"><FaStar /> {t('organic.reviewsTitle')}</span></div>
      {!reviews.total ? (
        <div className="soc-empty-box">{t('organic.reviewsNone')}</div>
      ) : (
        <div className="org-reviews">
          <div className="org-reviews-score">
            <span className="org-reviews-pct">{t('organic.reviewsRecommend', { pct })}</span>
            <span className="org-bar"><span style={{ width: `${pct}%`, background: '#10b981' }} /></span>
            <span className="org-reviews-count">{t('organic.reviewsCount', { n: numFmt.format(reviews.total) })}</span>
          </div>
          {reviews.latest?.length > 0 && (
            <ul className="org-reviews-list">
              {reviews.latest.slice(0, 2).map((r, i) => (
                <li key={i}>
                  <FaQuoteLeft className={r.positive ? 'pos' : 'neg'} />
                  <span className="org-review-text">{shortText(r.text, 180)}</span>
                  <span className="org-review-date">{longDate(r.date)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
